import { TopBarBadgeCount } from "~/types/topBarBadgeCount";
import { getRequest } from "~/apis/requests/useApiCall";

export const useTopBarBadgeStore = definePiniaStore("topBarBadge", {
  state: () => ({
    _unhandledCallsCount: 0,
    _newTicketsCount: 0,
    _newMessagesCount: 0,
    _overdueTaskCount: 0,
    _unReadNotificationCount: 0,
    _todayFollowUpIds: [] as number[],
    // Deploy-skew shim, see getTopBarBadgeCount: holds the old scalar count
    // while talking to a backend that does not send the ids yet, and is null
    // (i.e. silent) from the moment any writer commits real ids — that is done
    // in setTodayFollowUpIds so no caller can forget it.
    _todayFollowUpFallbackCount: null as number | null,
    // Latches true the first time this backend proves it sends ids, and never
    // goes back. A rolling deploy answers from old and new instances at once,
    // so without the latch one ids-less response re-arms the shim and pins the
    // badge to a stale scalar again while socket events move the ids
    // underneath it — flip-flopping for the length of the deploy.
    _todayFollowUpIdsSupported: false,
    // Sequencing for full-snapshot seeds of the id set. Every seed takes a
    // generation before its request leaves, and socket events that land while
    // that request is in flight are recorded here so the seed can replay them
    // on arrival. `true` = the socket added this id, `false` = it removed it.
    _followUpSeedGeneration: 0,
    _pendingFollowUpDeltas: {} as Record<number, boolean>,
    _topBarBadgeCount: {} as TopBarBadgeCount,
  }),
  actions: {
    async getTopBarBadgeCount() {
      // Claim the generation before the request is issued, not after it
      // answers: everything that happens to the id set from here until the
      // response lands belongs to this flight.
      const generation = this.beginTodayFollowUpSeed();
      const { response, error } = await getRequest<TopBarBadgeCount>(
        "/topbar/items/counts",
      );
      if (!error) {
        this.$state._topBarBadgeCount = response.data;

        this.$state._unhandledCallsCount =
          this.$state._topBarBadgeCount.unhandled_call_count;
        this.$state._newTicketsCount =
          this.$state._topBarBadgeCount.new_ticket_count;
        this.$state._newMessagesCount =
          this.$state._topBarBadgeCount.unread_conversation_count;
        this.$state._overdueTaskCount =
          this.$state._topBarBadgeCount.overdue_task_count;
        this.$state._unReadNotificationCount =
          this.$state._topBarBadgeCount.unread_notification_count;
        // Seed the id set rather than a number — but only when the backend
        // actually sent ids. A missing key is not the same answer as an empty
        // array: the array means "you have no follow-ups today", the missing
        // key means "this backend does not know about ids yet". Treating the
        // second as the first zeroed the id set and hid the badge outright for
        // every agent, silently, and re-confirmed it on every timer tick. The
        // two repos are separate deployables, so a frontend released ahead of
        // the backend hits exactly that. While ids are absent we fall back to
        // the scalar count the payload still carries, which is the pre-socket
        // badge behaviour. Silencing the shim is NOT done here: setting it is a
        // property of this response, but clearing it is a property of the id
        // set, and this is not the only writer of that set — the modal's fetch
        // is too. So setTodayFollowUpIds clears it, which means whichever
        // writer first proves it has real ids silences the shim for good; a
        // shim cleared only here would keep pinning the badge to a stale number
        // while socket events moved the ids underneath it.
        if (response.data.today_follow_up_ids === undefined) {
          // Only arm the shim while this backend has never sent ids. Once it
          // has, an ids-less response is a stale instance mid-deploy, not a
          // backend that lacks the feature, and honouring it would undo a live
          // id set.
          if (!this.$state._todayFollowUpIdsSupported) {
            this.$state._todayFollowUpFallbackCount = Number(
              this.$state._topBarBadgeCount.today_follow_up_count ?? 0,
            );
          }
        } else {
          this.setTodayFollowUpIds(
            response.data.today_follow_up_ids,
            generation,
          );
        }
      }
    },

    // mutations
    adjustUnhandledCallsCount(delta: number) {
      this._unhandledCallsCount = Math.max(
        0,
        this._unhandledCallsCount + delta,
      );
    },
    incrementTicketCount() {
      this._newTicketsCount += 1;
    },
    decrementTicketCount() {
      if (this._newTicketsCount > 0) this._newTicketsCount -= 1;
    },
    adjustMessageCount(value: number) {
      this._newMessagesCount = Math.max(0, this._newMessagesCount + value);
    },
    incrementOverdueTaskCount() {
      this._overdueTaskCount += 1;
    },
    incrementUnReadNotificationCount() {
      this._unReadNotificationCount += 1;
    },
    decrementUnReadNotificationCount() {
      if (this._unReadNotificationCount > 0) this._unReadNotificationCount -= 1;
    },
    resetUnReadNotificationCount() {
      this._unReadNotificationCount = 0;
    },
    // Today's follow-up list is exactly the set this badge counts, so whenever
    // the modal loads it the badge can be reconciled without another request.
    // We keep the ids and not a tally because the socket speaks in rows, not in
    // deltas: an event says "follow-up #42 is now (or is no longer) in your
    // today list". A +1/-1 counter double-counts that — the agent who creates a
    // follow-up for themselves already refreshed the count from their own POST
    // before the broadcast for that same row lands, and a reconnect can replay
    // one too. Adding an id that is already present is a no-op and removing one
    // that was never there is too, so the badge stays correct no matter how the
    // EVENTS are ordered or duplicated.
    //
    // What the set alone does NOT protect against is a full-snapshot seed
    // racing an event. A seed does not merge, it replaces, so a response the
    // server computed before the event happened will happily reinstate an id
    // the event just removed (or drop one it just added) when it lands after
    // it. Hence the generation stamp plus the replay log below: seeds are
    // sequenced against each other, and events that occur mid-flight are
    // re-applied on top of whatever snapshot the server sends back.
    beginTodayFollowUpSeed(): number {
      this._pendingFollowUpDeltas = {};
      this._followUpSeedGeneration += 1;
      return this._followUpSeedGeneration;
    },
    // Returns the ids actually committed to the set — snapshot plus replayed
    // deltas — or null when the response was discarded as stale. Callers that
    // hold a matching view of the same set (the modal's row array) rebuild it
    // from the returned ids, so the badge and the table can never disagree
    // about which follow-ups are in today's list.
    setTodayFollowUpIds(ids: number[], generation?: number): number[] | null {
      // A newer seed has already been issued, so this snapshot is known-stale
      // and there is nothing worth salvaging from it — the newer one will
      // commit shortly and will carry the same deltas.
      if (
        generation !== undefined &&
        generation !== this._followUpSeedGeneration
      ) {
        return null;
      }
      // Real ids are in hand, so the deploy-skew shim is done for good; see
      // getTopBarBadgeCount. Cleared only after the generation guard passes: a
      // discarded stale response has proved nothing about this backend.
      this._todayFollowUpFallbackCount = null;
      this._todayFollowUpIdsSupported = true;
      const nextIds = new Set(ids.map((id) => Number(id)));
      Object.entries(this._pendingFollowUpDeltas).forEach(([id, wasAdded]) => {
        if (wasAdded) {
          nextIds.add(Number(id));
        } else {
          nextIds.delete(Number(id));
        }
      });
      this._pendingFollowUpDeltas = {};
      this._todayFollowUpIds = [...nextIds];
      return this._todayFollowUpIds;
    },
    addTodayFollowUpId(id: number) {
      const followUpId = Number(id);
      if (!this._todayFollowUpIds.includes(followUpId)) {
        this._todayFollowUpIds.push(followUpId);
      }
      this._pendingFollowUpDeltas[followUpId] = true;
    },
    removeTodayFollowUpId(id: number) {
      const followUpId = Number(id);
      this._todayFollowUpIds = this._todayFollowUpIds.filter(
        (existingId) => existingId !== followUpId,
      );
      this._pendingFollowUpDeltas[followUpId] = false;
    },
  },
  getters: {
    unhandledCallsCount: (state) => state._unhandledCallsCount,
    newTicketsCount: (state) => state._newTicketsCount,
    newMessagesCount: (state) => state._newMessagesCount,
    overdueTaskCount: (state) => state._overdueTaskCount,
    unReadNotificationCount: (state) => state._unReadNotificationCount,
    todayFollowUpCount: (state) =>
      state._todayFollowUpFallbackCount ?? state._todayFollowUpIds.length,
  },
});
